import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, authedFetch } from '@/lib/api-client';
import { API_BASE } from '@/lib/api-base';

/**
 * Payroll Runs — the cycle through DRAFT → CALCULATED → APPROVED → LOCKED → PAID.
 * Each run is one (entity, pay-calendar) pair. Lines = one row per employee.
 */

export type RunStatus = 'DRAFT' | 'CALCULATED' | 'APPROVED' | 'LOCKED' | 'PAID' | 'CANCELLED';
export type RunType   = 'REGULAR' | 'BONUS' | 'SUPPLEMENTARY' | 'ADHOC' | string;

export type PayrollRun = {
  id: string;
  entity_id: string;
  pay_calendar_id: string;
  run_type: RunType;
  status: RunStatus;
  employee_count: number;
  total_gross: number | string;
  total_deductions: number | string;
  total_net: number | string;
  total_employer_contributions: number | string;
  total_pf_ee: number | string;
  total_pf_er: number | string;
  total_esic_ee: number | string;
  total_esic_er: number | string;
  total_pt: number | string;
  total_lwf_ee: number | string;
  total_lwf_er: number | string;
  total_tds: number | string;
  calculated_by: string | null;
  calculated_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  locked_by: string | null;
  locked_at: string | null;
  paid_at: string | null;
  notes: string | null;
  calc_errors: unknown[] | null;
  selected_employee_ids: string[] | null;
  skipped_employees: Record<string, unknown> | null;
  /** {employee_id: reason} for employees whose NET this run withholds. They
   *  are still calculated in full — statutory and TDS book normally for the
   *  month the salary was earned; only the payout is held. */
  held_employees: Record<string, string> | null;
  created_at: string;
  updated_at: string;
  // Enriched
  period_label: string;
  pay_period_start: string | null;
  pay_period_end:   string | null;
  entity_name: string;
  entity_code: string;
  entity_currency_code?: string;
  entity_attendance_mode: string;
  pay_calendar_status: string;
  /** True when attendance was edited after the last calculation — the run's
   *  lines are stale and need Revert + Recalculate to pick up changes. */
  attendance_stale?: boolean;
};

export type PayrollLine = {
  id: string;
  payroll_run_id: string;
  employee_id: string;
  salary_structure_id: string | null;
  annual_ctc: number | string | null;
  monthly_ctc: number | string | null;
  period_calendar_days: number;
  period_working_days: number;
  days_worked: number | string;
  lop_days: number | string;
  paid_days: number | string;
  ot_hours: number | string;
  proration_factor: number | string;
  proration_method: string;
  earnings: { short_code: string; name: string; amount: number; note?: string; prorated?: boolean; is_recurring?: boolean; is_adjustment?: boolean; formula_type?: string; in_gross?: boolean; taxable_only?: boolean; is_perquisite?: boolean }[];
  deductions: {
    short_code: string; name: string; amount: number; note?: string;
    /** TDS row only, when a pay item flagged "one-time tax" was paid: the
     *  same-month tax inside `amount`, the income it is on, and the rest. */
    one_time_tax?: number; one_time_income?: number; regular_tds?: number;
  }[];
  employer_contributions: { short_code: string; name: string; amount: number; note?: string }[];
  gross_pay: number | string;
  total_deductions_amount: number | string;
  net_pay: number | string;
  employer_total: number | string;
  pf_ee: number | string;
  pf_epf_er: number | string;
  pf_eps_er: number | string;
  pf_edli: number | string;
  pf_admin: number | string;
  esic_ee: number | string;
  esic_er: number | string;
  pt_amount: number | string;
  lwf_ee: number | string;
  lwf_er: number | string;
  tds_amount: number | string;
  calc_error: string | null;
  /** Salary hold. 'HOLD' means this line was computed in full but its net was
   *  withheld; 'PROCESSED' is everyone else. `salary_hold_released` is
   *  earlier months' holds paid out ON this line. */
  salary_hold_status?: 'HOLD' | 'PROCESSED' | null;
  salary_hold_amount?: number | string | null;
  salary_hold_reason?: string | null;
  salary_hold_released?: number | string | null;
  emp_id?: string;
  emp_name?: string;
  /** Employee profile fields, denormalised onto the line by the backend so the
   *  lines table can show org placement (department / designation / location /
   *  cost centre / DOJ) without a second fetch against the employee master. */
  department?: string | null;
  designation?: string | null;
  location?: string | null;
  cost_center?: string | null;
  date_of_joining?: string | null;
  gender?: string | null;
  date_of_birth?: string | null;
  employment_type?: string | null;
  work_state?: string | null;
  skill_type?: string | null;
  manager_emp_id?: string | null;
  pan?: string | null;
  pf_uan?: string | null;
  bank_account_no?: string | null;
  bank_ifsc?: string | null;
  /** "Yes" | "Fixed Amount" | "No" — the employee's configured PF ceiling,
   *  resolved SERVER-side by `pf_ceiling_flag` so the run table, the run's
   *  Excel download, the Employees list and the statutory Pay Register
   *  cannot disagree. Never re-derive it from pf_ceiling_mode here. */
  pf_ceiling?: string | null;
  /** Separation flags (Phase A). Set on the backend at lines-fetch time so
   *  the lines table can badge resigned employees with a link to their FnF
   *  case — prevents double-paying someone who's already been FnF-settled. */
  is_separating?: boolean;
  date_of_leaving?: string | null;
  fnf_case_id?: string | null;
  fnf_status?: 'OPEN' | 'COMPUTED' | 'APPROVED' | 'PAID' | 'FINALIZED' | 'CANCELLED' | null;
};

// ── List + filter ────────────────────────────────────────────────────────────

export type RunFilters = {
  entity_id?: string;
  pay_calendar_id?: string;
  status?: RunStatus;
  run_type?: RunType;
};

/** `opts.enabled` lets a caller hold the query until its filters are known.
 *  Without it, a component that resolves its entity asynchronously fires an
 *  unfiltered request for every run in the system first. */
export function usePayrollRuns(filters: RunFilters = {}, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['payroll-runs', filters],
    queryFn: () => api.get<PayrollRun[]>('/payroll-runs', filters as Record<string, string | undefined>),
    enabled: opts?.enabled ?? true,
  });
}

export function usePayrollRun(id: string | null | undefined) {
  return useQuery({
    queryKey: ['payroll-runs', 'detail', id],
    queryFn: () => api.get<PayrollRun>(`/payroll-runs/${id}`),
    enabled: !!id,
  });
}

export type RunLinesPage = {
  lines: PayrollLine[];
  total: number;
  page: number;
  page_size: number;
};

/**
 * Sweep PP.5 #11 — server-side paginated lines.
 *
 * Args:
 *   id         the run id
 *   opts.page  1-indexed page number (default 1)
 *   opts.pageSize  1..1000 rows (default 100)
 *   opts.q     optional substring filter against emp_id / name
 *
 * Returns `{ lines, total, page, page_size }`. `total` reflects the
 * post-search count so the Detail page can render "Showing N of T".
 *
 * Cache key includes paging + query so React-Query treats each page as
 * an independent cache entry. `keepPreviousData` smooths the page-change
 * transition (no flicker while the next page loads).
 */
/**
 * List employees who are active in this run's entity but didn't get a
 * PayrollRunLine on this run. Used by the Detail page's Skipped panel.
 *
 * Reasons:
 *   EXCLUDED_FROM_SELECTION  — explicit run.selected_employee_ids list
 *                              left this employee out
 *   NO_LINE                  — selection was ALL but engine produced
 *                              no line (Readiness blocked the calc)
 *   JOINED_AFTER_PERIOD      — DOJ > period_end
 *   LEFT_BEFORE_PERIOD       — DOL < period_start
 */
export type SkippedEmployee = {
  employee_id: string;
  emp_id: string;
  name: string;
  department: string | null;
  designation: string | null;
  date_of_joining: string | null;
  date_of_leaving: string | null;
  reason: 'EXCLUDED_FROM_SELECTION' | 'NO_LINE' | 'JOINED_AFTER_PERIOD' | 'LEFT_BEFORE_PERIOD';
  reason_detail: string;
};
export type SkippedEmployees = { employees: SkippedEmployee[]; total: number };

export function useSkippedEmployees(runId: string | null | undefined) {
  return useQuery({
    queryKey: ['payroll-runs', 'skipped', runId],
    queryFn: () => api.get<SkippedEmployees>(`/payroll-runs/${runId}/skipped-employees`),
    enabled: !!runId,
  });
}

/** Create a SUPPLEMENTARY run targeting this run's skipped employees. */
export function useSpawnSupplementaryForSkipped() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) =>
      api.post<PayrollRun>(`/payroll-runs/${runId}/spawn-skipped`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payroll-runs'] });
    },
  });
}

export function useRunLines(
  id: string | null | undefined,
  opts: { page?: number; pageSize?: number; q?: string } = {},
) {
  const page = opts.page ?? 1;
  const pageSize = opts.pageSize ?? 100;
  const q = opts.q ?? '';
  return useQuery({
    queryKey: ['payroll-runs', 'lines', id, page, pageSize, q],
    queryFn: () => api.get<RunLinesPage>(`/payroll-runs/${id}/lines`, {
      page, page_size: pageSize,
      ...(q ? { q } : {}),
    }),
    enabled: !!id,
    placeholderData: (prev) => prev,    // smooth page transitions
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────

export type CreateRunBody = {
  entity_id: string;
  pay_calendar_id: string;
  run_type?: RunType;
  notes?: string;
  employee_ids?: string[];
};

export function useCreatePayrollRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRunBody) => api.post<PayrollRun>('/payroll-runs', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

/** Sweep U.3: backend now returns a CalculateResponse envelope. For entities
 *  ≤ ASYNC_CALC_THRESHOLD_EMPS (default 200) the calc runs inline and `run`
 *  is populated; larger entities get `{job: {status: 'PENDING', ...}, run: null}`
 *  and the UI must poll `useCalculationJob()` until the job completes. */
export type CalculationJob = {
  id: string;
  run_id: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  employee_count: number | null;
  actor: string | null;
  error: string | null;
  enqueued_at: string;
  started_at: string | null;
  completed_at: string | null;
};

export type CalculateResponse = {
  job: CalculationJob;
  run: PayrollRun | null;
};

export function useCalculateRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<CalculateResponse>(`/payroll-runs/${id}/calculate`),
    onSuccess: (data, id) => {
      // Only invalidate when the calc actually finished. For the async
      // path the run hasn't changed yet — invalidating now would make
      // the UI refetch the stale snapshot. The polling hook will
      // invalidate when the job flips to COMPLETED.
      if (data.job.status === 'COMPLETED' || data.job.status === 'FAILED') {
        qc.invalidateQueries({ queryKey: ['payroll-runs'] });
        qc.invalidateQueries({ queryKey: ['payroll-runs', 'detail', id] });
        qc.invalidateQueries({ queryKey: ['payroll-runs', 'lines', id] });
      }
    },
  });
}

/** Poll the calculation_jobs table for a queued run. Used by RunWizard
 *  when /calculate returns a PENDING job. Stops polling automatically
 *  on COMPLETED / FAILED — the React Query refetchInterval callback
 *  returns false to halt. Polls every 3s while running. */
export function useCalculationJob(runId: string, jobId: string | null | undefined) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['payroll-runs', 'calc-job', runId, jobId],
    queryFn: async () => {
      const job = await api.get<CalculationJob>(
        `/payroll-runs/${runId}/calculate/jobs/${jobId}`,
      );
      // Side-effect: when the job finishes, invalidate the run + lines
      // queries so the wizard sees fresh data without an extra click.
      if (job.status === 'COMPLETED' || job.status === 'FAILED') {
        qc.invalidateQueries({ queryKey: ['payroll-runs'] });
        qc.invalidateQueries({ queryKey: ['payroll-runs', 'detail', runId] });
        qc.invalidateQueries({ queryKey: ['payroll-runs', 'lines', runId] });
      }
      return job;
    },
    enabled: !!jobId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      if (s === 'COMPLETED' || s === 'FAILED') return false;
      return 3000;
    },
  });
}

export function useChangeRunStatus() {
  const qc = useQueryClient();
  return useMutation({
    // Sweep Q.3: callers should pass `updatedAt` (the run's last-loaded
    // updated_at) so the backend can optimistic-lock-check via If-Match.
    // Without it, a stale "Approve" click could overwrite a concurrent
    // "Reject" — backend rejects with 412 and the UI must reload first.
    mutationFn: (vars: { id: string; status: RunStatus; actor?: string; notes?: string; updatedAt?: string }) =>
      api.patch<PayrollRun>(`/payroll-runs/${vars.id}/status`, {
        status: vars.status, actor: vars.actor ?? 'ui', notes: vars.notes,
      }, vars.updatedAt),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['payroll-runs'] });
      qc.invalidateQueries({ queryKey: ['payroll-runs', 'detail', vars.id] });
    },
  });
}

export function useDeletePayrollRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/payroll-runs/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

export function useRevertRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<PayrollRun>(`/payroll-runs/${id}/revert`),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['payroll-runs'] });
      qc.invalidateQueries({ queryKey: ['payroll-runs', 'detail', id] });
      qc.invalidateQueries({ queryKey: ['payroll-runs', 'lines', id] });
    },
  });
}

// ── Bulk delete ──────────────────────────────────────────────────────────────

/**
 * Bulk-delete payload. `force: true` bypasses the LOCKED/PAID guard
 * so terminal-status runs (committed payouts) can be hard-deleted
 * too — used by the Cleanup menu's "DELETE LOCKED runs" item with a
 * second-level confirm. Default behaviour stays safe.
 */
export type BulkDeleteBody =
  | ({ mode: 'all' } & { force?: boolean })
  | ({ mode: 'status'; statuses: RunStatus[] } & { force?: boolean })
  | ({ ids: string[] } & { force?: boolean });

export function useBulkDeleteRuns() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkDeleteBody) =>
      api.post<{ deleted: number; lines_deleted: number }>('/payroll-runs/bulk-delete', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll-runs'] }),
  });
}

// ── Employee selection (DRAFT only) ─────────────────────────────────────────

export type SelectionUpdate = {
  selected_employee_ids?: string[] | null;
  skipped_employees?: Record<string, string> | null;
};

export function useUpdateRunSelection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: SelectionUpdate }) =>
      api.patch<PayrollRun>(`/payroll-runs/${vars.id}/selection`, vars.body),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['payroll-runs', 'detail', vars.id] });
      qc.invalidateQueries({ queryKey: ['payroll-runs', 'readiness', vars.id] });
    },
  });
}

// ── Readiness check ─────────────────────────────────────────────────────────

export type ReadinessIssue = {
  level: 'BLOCK' | 'WARN' | 'INFO';
  code: string;
  msg: string;
};

export type ReadinessRow = {
  employee_id: string;
  emp_id: string;
  name: string;
  department: string | null;
  designation: string | null;
  status: 'READY' | 'WARN' | 'BLOCKED';
  issues: ReadinessIssue[];
  salary_structure: string | null;
  annual_ctc: number | null;
  has_bank: boolean;
  has_pan: boolean;
  state_code: string | null;
  has_attendance: boolean;
};

export type ReadinessReport = {
  run_id: string;
  run_status: RunStatus;
  run_checks: { key: string; ok: boolean; critical: boolean; title: string; sub: string }[];
  employees: ReadinessRow[];
  summary: { total: number; ready: number; warn: number; blocked: number };
};

export function useRunReadiness(id: string | null | undefined) {
  return useQuery({
    queryKey: ['payroll-runs', 'readiness', id],
    queryFn: () => api.get<ReadinessReport>(`/payroll-runs/${id}/readiness`),
    enabled: !!id,
  });
}

// ── Pre-approval review panels ──────────────────────────────────────────────
// Salary deviation · New joiners · Missing salary structure. All three are
// read-only derivations served by the run API — no local caching beyond React
// Query. Each hook has a `summary_only` twin used by the wizard cards so the
// counters don't pull the whole row set on every render.

export type DeviationStatus =
  | 'FLAGGED' | 'INCREASED' | 'DECREASED' | 'UNCHANGED' | 'NEW_IN_RUN' | 'NOT_IN_RUN';

export type DeviationComponentChange = {
  short_code: string;
  name: string;
  previous: number;
  current: number;
  delta: number;
};

export type DeviationRow = {
  employee_id: string;
  emp_id: string | null;
  name: string;
  department: string | null;
  designation: string | null;
  date_of_joining: string | null;
  date_of_leaving: string | null;
  in_scope: boolean;
  status: DeviationStatus;
  current_net: number | null;
  previous_net: number | null;
  current_gross: number | null;
  previous_gross: number | null;
  current_ctc: number | null;
  previous_ctc: number | null;
  current_lop: number | null;
  previous_lop: number | null;
  delta_net: number;
  delta_gross: number;
  /** null when the previous net was 0 — no baseline to divide by. */
  delta_net_pct: number | null;
  delta_gross_pct: number | null;
  reasons: string[];
  component_changes: DeviationComponentChange[];
  calc_error: string | null;
};

export type SalaryDeviationReport = {
  run_id: string;
  run_status: RunStatus;
  period_label: string | null;
  threshold_pct: number;
  comparison_available: boolean;
  reason: string | null;
  reason_detail: string | null;
  previous_run: {
    id: string;
    status: RunStatus;
    period_label: string | null;
    period_start: string | null;
    period_end: string | null;
  } | null;
  employees: DeviationRow[];
  summary: {
    total: number; flagged: number; increased: number; decreased: number;
    unchanged: number; new_in_run: number; not_in_run: number;
    net_delta_total: number; gross_delta_total: number;
  };
};

export function useSalaryDeviation(
  id: string | null | undefined,
  opts: { thresholdPct?: number; summaryOnly?: boolean; enabled?: boolean } = {},
) {
  const threshold = opts.thresholdPct ?? 10;
  const summaryOnly = opts.summaryOnly ?? false;
  return useQuery({
    queryKey: ['payroll-runs', 'salary-deviation', id, threshold, summaryOnly],
    queryFn: () => api.get<SalaryDeviationReport>(
      `/payroll-runs/${id}/salary-deviation`,
      { threshold_pct: String(threshold), summary_only: String(summaryOnly) },
    ),
    enabled: !!id && (opts.enabled ?? true),
    staleTime: 30_000,
  });
}

export type JoinerGap = { level: 'BLOCK' | 'WARN'; code: string; msg: string };

export type NewJoinerRow = {
  employee_id: string;
  emp_id: string;
  name: string;
  department: string | null;
  designation: string | null;
  email: string | null;
  date_of_joining: string | null;
  category: 'JOINED_IN_PERIOD' | 'FIRST_PAYROLL';
  days_in_period: number | null;
  period_calendar_days: number;
  pay_group: string | null;
  salary_structure: string | null;
  annual_ctc: number | null;
  in_scope: boolean;
  in_run: boolean;
  paid_days: number | null;
  lop_days: number | null;
  gross_pay: number | null;
  net_pay: number | null;
  proration_factor: number | null;
  calc_error: string | null;
  gaps: JoinerGap[];
  severity: 'BLOCK' | 'WARN' | 'OK';
};

export type NewJoinersReport = {
  run_id: string;
  period_label: string | null;
  period_start?: string;
  period_end?: string;
  employees: NewJoinerRow[];
  summary: {
    total: number; joined_in_period: number; first_payroll: number;
    with_gaps: number; not_in_run: number;
  };
  reason: string | null;
  reason_detail: string | null;
};

export function useNewJoiners(
  id: string | null | undefined,
  opts: { summaryOnly?: boolean; enabled?: boolean } = {},
) {
  const summaryOnly = opts.summaryOnly ?? false;
  return useQuery({
    queryKey: ['payroll-runs', 'new-joiners', id, summaryOnly],
    queryFn: () => api.get<NewJoinersReport>(
      `/payroll-runs/${id}/new-joiners`, { summary_only: String(summaryOnly) },
    ),
    enabled: !!id && (opts.enabled ?? true),
    staleTime: 30_000,
  });
}

export type MissingStructureReason =
  | 'NO_STRUCTURE' | 'STRUCTURE_NOT_FOUND' | 'STRUCTURE_INACTIVE' | 'NO_CTC' | 'DIRECT_PAY_ONLY';

export type MissingStructureRow = {
  employee_id: string;
  emp_id: string;
  name: string;
  department: string | null;
  designation: string | null;
  date_of_joining: string | null;
  employment_type: string | null;
  status: string;
  pay_group: string | null;
  has_pay_group: boolean;
  salary_structure: string | null;
  annual_ctc: number | null;
  direct_pay_items: number;
  reason: MissingStructureReason;
  reason_detail: string;
  severity: 'BLOCK' | 'INFO';
  in_scope: boolean;
  in_run: boolean;
};

export type MissingStructureReport = {
  run_id: string;
  run_status: RunStatus;
  period_label: string | null;
  pay_group: string | null;
  employees: MissingStructureRow[];
  summary: {
    total: number; blocking: number; info: number;
    in_this_run: number; roster_total: number;
  };
};

export function useEmployeesWithoutStructure(
  id: string | null | undefined,
  opts: { summaryOnly?: boolean; enabled?: boolean } = {},
) {
  const summaryOnly = opts.summaryOnly ?? false;
  return useQuery({
    queryKey: ['payroll-runs', 'without-structure', id, summaryOnly],
    queryFn: () => api.get<MissingStructureReport>(
      `/payroll-runs/${id}/employees-without-structure`, { summary_only: String(summaryOnly) },
    ),
    enabled: !!id && (opts.enabled ?? true),
    staleTime: 30_000,
  });
}

// ── Frozen attendance ───────────────────────────────────────────────────────
// Attendance is a live table: an applied LOP reversal flips the day to Present
// on the attendance page, which is intended. The side effect is that after a
// reversal nothing explains why that month's payslip deducted the day. When a
// run LOCKS, the attendance it was calculated on is copied and kept, and this
// endpoint reads that copy back next to what attendance says today.

export type FrozenAttendanceDay = {
  attendance_date: string;
  /** What payroll counted. */
  status_code: string;
  is_half_day: boolean;
  in_time: string | null;
  out_time: string | null;
  total_hours: number | null;
  ot_hours: number | null;
  source: string | null;
  notes: string | null;
  /** What the attendance page says NOW; null if the live row is gone. */
  live_status_code: string | null;
  live_is_half_day: boolean | null;
  /** Live no longer matches the freeze — in practice, an applied reversal. */
  changed: boolean;
};

export type FrozenAttendanceEmployee = {
  employee_id: string;
  emp_id: string;
  emp_name: string;
  department: string | null;
  designation: string | null;
  frozen_lop_days: number;
  frozen_paid_days: number;
  changed_days: number;
  days: FrozenAttendanceDay[];
};

export type FrozenAttendance = {
  run_id: string;
  run_status: RunStatus;
  /** true = a real freeze exists. false = never captured (run still open, or
   *  locked before this shipped) and `employees` is LIVE attendance. */
  captured: boolean;
  is_live_fallback: boolean;
  captured_at: string | null;
  captured_by: string | null;
  captured_on_status: string | null;
  row_count: number;
  employee_count: number;
  /** The days this run pays for. On a 21–20 pay group the September run
   *  reads 21 Aug → 20 Sep, which is why the grid is not a calendar month. */
  window_start: string | null;
  window_end: string | null;
  is_custom_cycle: boolean;
  changed_days_total: number;
  employees: FrozenAttendanceEmployee[];
};

export function useFrozenAttendance(
  id: string | null | undefined,
  opts: { employeeId?: string; enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['payroll-runs', 'frozen-attendance', id, opts.employeeId ?? null],
    queryFn: () => api.get<FrozenAttendance>(
      `/payroll-runs/${id}/frozen-attendance`,
      opts.employeeId ? { employee_id: opts.employeeId } : {},
    ),
    enabled: !!id && (opts.enabled ?? true),
    staleTime: 30_000,
  });
}


// ── ESIC continuation acknowledgement ───────────────────────────────────────
// CTC-revised ESIC employees keep having ESIC deducted until the contribution
// period ends. HR must acknowledge this note before a run can be APPROVED.

export type EsicContinuationEmployee = {
  employee_id: string;
  emp_code: string | null;
  name: string | null;
  esic_ee: number;
  period_label: string | null;
  note: string;
  acknowledged: boolean;
};

export type EsicContinuationNotes = {
  run_id: string;
  employees: EsicContinuationEmployee[];
  unacknowledged_count: number;
  all_acknowledged: boolean;
};

export function useEsicContinuationNotes(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['payroll-runs', 'esic-continuation', id],
    queryFn: () => api.get<EsicContinuationNotes>(`/payroll-runs/${id}/esic-continuation-notes`),
    enabled: !!id && enabled,
  });
}

export function useAckEsicContinuation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { runId: string; employeeIds?: string[] }) =>
      api.post<EsicContinuationNotes>(
        `/payroll-runs/${vars.runId}/esic-continuation-notes/ack`,
        vars.employeeIds ? { employee_ids: vars.employeeIds } : {},
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['payroll-runs', 'esic-continuation', vars.runId] });
      qc.invalidateQueries({ queryKey: ['payroll-runs', 'detail', vars.runId] });
    },
  });
}

// ── Bank file formats ───────────────────────────────────────────────────────

export type BankFormat = { code: string; label: string };

export function useBankFileFormats(id: string | null | undefined) {
  return useQuery({
    queryKey: ['payroll-runs', 'bank-formats', id],
    queryFn: () => api.get<BankFormat[]>(`/payroll-runs/${id}/bank-file/formats`),
    enabled: !!id,
  });
}

// ── Adjustments (one-off lines added to a run before calculation) ───────────

export type AdjustmentType =
  | 'ARREAR' | 'BONUS' | 'RECOVERY' | 'DEDUCTION' | 'ADVANCE'
  // Expanded vocabulary so HR can post explicit incentives /
  // reimbursements / generic one-offs without overloading the
  // BONUS / DEDUCTION buckets. Backend tax treatment differs:
  //   INCENTIVE       — taxable; folded into one_time_gross for marginal TDS
  //   REIMBURSEMENT   — non-taxable earning; doesn't push TDS slabs
  //   OTHER_EARNING   — honours the row's `is_taxable` flag
  //   OTHER_DEDUCTION — generic deduction line
  | 'INCENTIVE' | 'REIMBURSEMENT' | 'OTHER_EARNING' | 'OTHER_DEDUCTION';

export type RunAdjustment = {
  id: string;
  payroll_run_id: string;
  employee_id: string;
  adjustment_type: AdjustmentType;
  amount: number | string;
  pay_item_id: string | null;
  description: string;
  reference_period: string | null;
  is_taxable: boolean;
  created_by: string | null;
  emp_name: string;
  emp_id_code: string;
  created_at: string;
  updated_at: string;
};

export type AdjustmentWritable = {
  employee_id: string;
  adjustment_type: AdjustmentType;
  amount: number;
  description: string;
  pay_item_id?: string | null;
  reference_period?: string | null;
  is_taxable?: boolean;
};

export function useRunAdjustments(runId: string | null | undefined) {
  return useQuery({
    queryKey: ['payroll-runs', 'adjustments', runId],
    queryFn: () => api.get<RunAdjustment[]>(`/payroll-runs/${runId}/adjustments`),
    enabled: !!runId,
  });
}

export function useCreateAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { runId: string; body: AdjustmentWritable }) =>
      api.post<RunAdjustment>(`/payroll-runs/${vars.runId}/adjustments`, vars.body),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['payroll-runs', 'adjustments', vars.runId] });
      qc.invalidateQueries({ queryKey: ['payroll-runs', 'detail', vars.runId] });
    },
  });
}

export function useUpdateAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { runId: string; adjId: string; body: AdjustmentWritable }) =>
      api.put<RunAdjustment>(`/payroll-runs/${vars.runId}/adjustments/${vars.adjId}`, vars.body),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['payroll-runs', 'adjustments', vars.runId] });
    },
  });
}

/**
 * Sweep PP.5 #15 — bulk-delete adjustments on a single run.
 *
 * Calls POST /payroll-runs/{runId}/adjustments/bulk-delete with a body of
 * `{ ids: [...] }`. Server validates the run isn't LOCKED/PAID and
 * silently skips ids that don't belong to the run (returned in `skipped`).
 *
 * Returns `{ deleted, skipped }`. On success we invalidate the run's
 * adjustment list so the panel re-fetches.
 */
export type BulkDeleteAdjustmentsResult = { deleted: number; skipped: string[] };

export function useBulkDeleteAdjustments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { runId: string; ids: string[] }) =>
      api.post<BulkDeleteAdjustmentsResult>(
        `/payroll-runs/${vars.runId}/adjustments/bulk-delete`,
        { ids: vars.ids },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['payroll-runs', 'adjustments', vars.runId] });
    },
  });
}

export function useDeleteAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { runId: string; adjId: string }) =>
      api.delete<void>(`/payroll-runs/${vars.runId}/adjustments/${vars.adjId}`),
    // Sweep PP.3 #16 — optimistic delete. Previously the row stayed visible
    // until the round trip + invalidation completed (~500ms on a slow link).
    // Snapshot the cache, remove the row immediately, and roll back on
    // error so the panel feels instant.
    onMutate: async (vars) => {
      const key = ['payroll-runs', 'adjustments', vars.runId] as const;
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<RunAdjustment[]>(key);
      if (prev) {
        qc.setQueryData<RunAdjustment[]>(
          key,
          prev.filter((a) => a.id !== vars.adjId),
        );
      }
      return { prev };
    },
    onError: (_e, vars, ctx) => {
      const key = ['payroll-runs', 'adjustments', vars.runId];
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['payroll-runs', 'adjustments', vars.runId] });
    },
  });
}

// ── Authenticated file download helper ──────────────────────────────────────

/**
 * Fetch a binary endpoint with our auth headers attached, then trigger a
 * browser download. Used for bank-file CSVs, payslip PDFs and the payslip ZIP
 * — none of which fit through the JSON-only `api` helper.
 */
// ── Sweep X/AA.2 — bulk journal export (cross-currency GL) ──────────────────

export type JournalListRow = {
  id: string;
  payroll_run_id: string;
  entity_id: string;
  period_label: string;
  journal_date: string;       // YYYY-MM-DD
  currency: string;           // ISO 4217
  total_debit: string;        // decimal as string
  total_credit: string;
  status: string;             // DRAFT | POSTED | REVERSED
  created_at: string | null;
};

export type JournalListFilters = {
  entity_id?: string;
  currency?: string;
  date_from?: string;
  date_to?: string;
  status?: string;
};

/** Browse the bulk journal list filtered by entity / currency / date /
 *  status. Drives the "Export GL journals" dialog — operators preview
 *  the matching journals before pulling the CSV. */
export function useJournalList(filters: JournalListFilters, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['payroll-journals', 'list', filters],
    queryFn: () => api.get<JournalListRow[]>(
      '/payroll-runs/journals',
      filters as Record<string, string | undefined>,
    ),
    enabled: opts?.enabled ?? true,
  });
}

/** Trigger the bulk CSV download honouring the same filter set. The
 *  backend streams a concatenated CSV (header once, then every
 *  matching journal's rows). Filename is server-generated and contains
 *  the currency filter for the operator to spot mistakes. */
export async function downloadBulkJournalsCsv(filters: JournalListFilters): Promise<void> {
  // downloadFile only accepts string-valued params, so coerce undefineds out.
  const params: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') params[k] = String(v);
  }
  await downloadFile('/payroll-runs/journals/export.csv', params);
}

export async function downloadFile(path: string, params?: Record<string, string>): Promise<void> {
  const qs = params
    ? '?' + Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join('&')
    : '';
  const res = await authedFetch(`${API_BASE}${path}${qs}`, { method: 'GET' });
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { body = await res.text().catch(() => null); }
    throw new ApiError(res.status, body);
  }

  // Pull filename out of Content-Disposition (server already provides one)
  const cd = res.headers.get('Content-Disposition') || '';
  const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  const filename = match ? decodeURIComponent(match[1]) : 'download';

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
